/**
 * ConfirmDialog — replaces `window.confirm` with a proper modal.
 *
 * Two usage patterns:
 *
 * 1. Imperative (drop-in replacement for `window.confirm`):
 *
 *      import { confirmDialog } from "@/components/ConfirmDialog";
 *      if (await confirmDialog({
 *        title: "Delete this property?",
 *        description: "Cannot be undone.",
 *        confirmLabel: "Yes, delete",
 *        destructive: true,
 *      })) {
 *        await api.delete(...);
 *      }
 *
 * 2. Declarative:
 *
 *      <ConfirmDialog open={...} onConfirm={...} onCancel={...} ... />
 *
 * A single portal-mounted host is auto-created the first time
 * `confirmDialog()` is called. It's always available anywhere in the
 * tree, no provider needed.
 */
import { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";

export function ConfirmDialog({
  open,
  title = "Are you sure?",
  description = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  onCancel,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
      if (e.key === "Enter") onConfirm?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onConfirm, onCancel]);

  if (!open) return null;
  return (
    <div
      data-testid="confirm-dialog"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5">
          <h2 id="confirm-title" className="text-lg font-bold text-slate-900" data-testid="confirm-title">
            {title}
          </h2>
          {description && (
            <p className="mt-2 text-sm text-slate-600 whitespace-pre-line" data-testid="confirm-description">
              {description}
            </p>
          )}
        </div>
        <div className="flex gap-2 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            type="button"
            onClick={onCancel}
            data-testid="confirm-cancel"
            className="flex-1 py-2.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-100 text-sm font-medium text-slate-800"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid="confirm-ok"
            autoFocus
            className={`flex-1 py-2.5 rounded-lg text-sm font-semibold text-white ${
              destructive
                ? "bg-red-600 hover:bg-red-700"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Imperative helper — returns a Promise<boolean>
// ---------------------------------------------------------------------------
let _hostRoot = null;

function _ensureHost() {
  if (_hostRoot) return _hostRoot;
  const node = document.createElement("div");
  node.setAttribute("data-crc-confirm-host", "");
  document.body.appendChild(node);
  _hostRoot = createRoot(node);
  return _hostRoot;
}

function ImperativeMount({ initial, resolve }) {
  const [state, setState] = useState({ ...initial, open: true });
  const finish = (result) => {
    setState((s) => ({ ...s, open: false }));
    // Give the fade-out a beat, then resolve.
    setTimeout(() => resolve(result), 0);
  };
  return (
    <ConfirmDialog
      {...state}
      onConfirm={() => finish(true)}
      onCancel={() => finish(false)}
    />
  );
}

export function confirmDialog(opts = {}) {
  return new Promise((resolve) => {
    const root = _ensureHost();
    root.render(
      <ImperativeMount
        initial={{
          title: opts.title || "Are you sure?",
          description: opts.description || "",
          confirmLabel: opts.confirmLabel || "Confirm",
          cancelLabel: opts.cancelLabel || "Cancel",
          destructive: !!opts.destructive,
        }}
        resolve={(result) => {
          // Unmount after resolution so we don't keep a stale dialog in the DOM.
          resolve(result);
          setTimeout(() => root.render(null), 0);
        }}
      />,
    );
  });
}
